import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import api, { Me, setDevToken } from '../services/api';

interface AuthCtx {
  me: Me | null;
  loading: boolean;
  signIn: () => void;
  signOut: () => void;
  hasRole: (...roles: string[]) => boolean;
}

const Ctx = createContext<AuthCtx>({
  me: null,
  loading: true,
  signIn: () => {},
  signOut: () => {},
  hasRole: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const probe = async () => {
    try {
      const r = await api.get<Me>('/me');
      setMe(r.data);
    } catch {
      setMe(null);
    }
  };

  useEffect(() => {
    // Dev fallback: a `?token=` query param (e.g. from a local Keycloak
    // password-grant helper) is kept in memory and sent as a bearer. In
    // production auth rides on the oauth2-proxy cookie + ForwardAuth.
    const params = new URLSearchParams(window.location.search);
    const devToken = params.get('token');
    if (devToken) {
      setDevToken(devToken);
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url);
    }
    probe().finally(() => setLoading(false));
  }, []);

  const signIn = () => {
    window.location.href = '/oauth2/sign_in?rd=' + encodeURIComponent(window.location.pathname);
  };

  const signOut = () => {
    setDevToken(null);
    setMe(null);
    window.location.href = '/oauth2/sign_out';
  };

  const hasRole = (...roles: string[]) => !!me && roles.some((r) => me.roles.includes(r));

  return (
    <Ctx.Provider value={{ me, loading, signIn, signOut, hasRole }}>{children}</Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
